/**
 * Cheap, driver-bounded liveness probe for a Playwright page.
 *
 * When a per-tool timeout fires, a Playwright op (`ariaSnapshot`, `screenshot`,
 * `stabilize`) may be orphaned and keep running for minutes on a jammed
 * renderer — there is no Playwright API to cancel it. The only reliable way to
 * kill the orphan is to close the page. But we must NOT close a page that is
 * merely slow-but-progressing. This probe is the gate: a trivial
 * `page.evaluate(() => 1)` bounded by a driver-side cap (so a blocked main
 * thread can't hang the probe itself).
 *
 *  - Resolves `1` within the cap  → {@link Liveness.Alive} (spare the page).
 *  - Cap fires / eval throws       → {@link Liveness.Unresponsive} (tear down).
 *  - Page closed / context gone    → {@link Liveness.Closed}.
 *
 * Pure over a `Page`-shaped object so it is unit-testable with a fake page and
 * no real browser.
 *
 * @module playwright-client/liveness-probe
 */

import { evaluateWithCap, EVALUATE_CAPPED } from './evaluate-with-cap.js';

/**
 * Probe outcome. Deliberately a string-literal union plus a same-named const of
 * those literals — NOT a `enum`. The union keeps values assignable to the plain
 * `'alive' | 'unresponsive' | 'closed'` return contract on
 * `IBrowserClient.probeLiveness` (a string enum is nominal and wouldn't be), and
 * the const gives callers named members (`Liveness.Alive`) without enum friction.
 * The `type Liveness` and `const Liveness` share a name on purpose; reference the
 * value (`Liveness.Alive`) and the type (`: Liveness`) as needed.
 */
export type Liveness = 'alive' | 'unresponsive' | 'closed';
export const Liveness = {
  Alive: 'alive',
  Unresponsive: 'unresponsive',
  Closed: 'closed',
} as const;

/** Minimal surface of a Playwright `Page` this probe needs (eases mocking). */
export interface ProbeablePage {
  isClosed(): boolean;
  evaluate<R>(fn: () => R): Promise<R>;
}

const CLOSURE_HINTS = [
  'target closed',
  'has been closed',
  'browser has been closed',
  'execution context was destroyed',
  'browser closed',
  'context closed',
];

function looksClosed(err: unknown): boolean {
  const message = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return CLOSURE_HINTS.some((h) => message.includes(h));
}

/**
 * Probe whether `page`'s main thread can service a trivial evaluate within
 * `capMs`. Never throws.
 */
export async function probePageLiveness(
  page: ProbeablePage | null,
  capMs: number
): Promise<Liveness> {
  if (!page || page.isClosed()) return Liveness.Closed;
  try {
    // Function form (Runtime.callFunctionOn) sidesteps in-page eval policy,
    // matching every other evaluate in the client.
    const result = await evaluateWithCap(capMs, () => page.evaluate(() => 1));
    if (result === EVALUATE_CAPPED) return Liveness.Unresponsive;
    return Liveness.Alive;
  } catch (err) {
    // A closure error means the page/context is gone (Closed); any other eval
    // failure on an open page is conservatively treated as Unresponsive — the
    // caller's worst case is one re-navigation, far cheaper than an orphan.
    return looksClosed(err) ? Liveness.Closed : Liveness.Unresponsive;
  }
}
